import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import Navbar from "@/components/Navbar";
import { FileText, Mic, Globe, Type, Trash2, Plus, Calendar, Search, X } from "lucide-react";
import { toast } from "sonner";
import StudyStreak from "@/components/StudyStreak";
import WeakTopicsStrip from "@/components/WeakTopicsStrip";

const iconFor = (t) => {
  if (t === "pdf") return FileText;
  if (t === "url") return Globe;
  if (t === "audio") return Mic;
  return Type;
};

const emptyImg = "https://images.unsplash.com/photo-1639413665566-2f75adf7b7ca?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHw0fHxjbGVhbiUyMGRlc2slMjB3b3Jrc3BhY2V8ZW58MHx8fHwxNzgzOTM4MzAyfDA&ixlib=rb-4.1.0&q=85";

function useDebounced(value, delay = 300) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

export default function Dashboard() {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tags, setTags] = useState([]);
  const [query, setQuery] = useState("");
  const [activeTag, setActiveTag] = useState(null);
  const [activeFormat, setActiveFormat] = useState(null); // "lecture" | "meeting" | null
  const debounced = useDebounced(query, 300);

  const params = useMemo(() => {
    const p = {};
    if (debounced.trim()) p.q = debounced.trim();
    if (activeTag) p.tag = activeTag;
    if (activeFormat) p.format = activeFormat;
    return p;
  }, [debounced, activeTag, activeFormat]);

  const load = async () => {
    setLoading(true);
    try {
      const [notesRes, tagsRes] = await Promise.all([
        api.get("/notes", { params }),
        api.get("/notes/tags"),
      ]);
      setNotes(notesRes.data);
      setTags(tagsRes.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [params]); // eslint-disable-line react-hooks/exhaustive-deps

  const remove = async (id) => {
    if (!window.confirm("Delete this note?")) return;
    try {
      await api.delete(`/notes/${id}`);
      setNotes((prev) => prev.filter((n) => n.id !== id));
      toast.success("Note deleted");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const clearAll = () => {
    setQuery("");
    setActiveTag(null);
    setActiveFormat(null);
  };

  const anyFilter = query || activeTag || activeFormat;

  return (
    <div className="min-h-screen bg-[#F0F9FF]">
      <Navbar />

      <main className="max-w-7xl mx-auto px-6 py-12">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">/ Library</p>
            <h1 className="mt-2 font-heading font-black text-4xl sm:text-6xl tracking-tighter">Your Notes</h1>
            <p className="mt-3 text-zinc-600">{notes.length} match{notes.length === 1 ? "" : "es"} · condensed & revision-ready</p>
          </div>
          <Link
            data-testid="cta-new-note"
            to="/app/new"
            className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest px-5 py-3 bg-[#3B82F6] text-white btn-brutal"
          >
            <Plus className="w-4 h-4" /> New Note
          </Link>
        </div>

        <StudyStreak />
        <WeakTopicsStrip />

        {/* Filters */}
        <div className="mt-10 space-y-4">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <input
              data-testid="search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search titles, summaries, keywords…"
              className="w-full pl-11 pr-11 py-3 bg-white border-2 border-black focus:outline-none focus:ring-2 focus:ring-[#3B82F6] focus:ring-offset-2"
            />
            {query && (
              <button
                data-testid="clear-search"
                onClick={() => setQuery("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-black hover:text-white transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-[11px] uppercase tracking-widest text-zinc-500">Format:</span>
            {[
              { id: null, label: "All" },
              { id: "lecture", label: "Lecture" },
              { id: "meeting", label: "Meeting" },
            ].map((f) => (
              <button
                key={f.label}
                data-testid={`filter-format-${f.id || "all"}`}
                onClick={() => setActiveFormat(f.id)}
                className={`font-mono text-[11px] uppercase tracking-widest px-3 py-1.5 border transition-colors ${
                  activeFormat === f.id
                    ? "bg-black text-white border-black"
                    : "bg-white border-black/20 hover:border-black"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>

          {tags.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-mono text-[11px] uppercase tracking-widest text-zinc-500">Tags:</span>
              {tags.slice(0, 24).map((t) => {
                const on = activeTag === t.tag;
                return (
                  <button
                    key={t.tag}
                    data-testid={`filter-tag-${t.tag}`}
                    onClick={() => setActiveTag(on ? null : t.tag)}
                    className={`font-mono text-[11px] uppercase tracking-wider px-3 py-1.5 border transition-colors ${
                      on
                        ? "bg-[#3B82F6] text-white border-[#3B82F6]"
                        : "bg-white border-black/20 hover:border-black"
                    }`}
                  >
                    {t.tag} <span className="opacity-60">· {t.count}</span>
                  </button>
                );
              })}
            </div>
          )}

          {anyFilter && (
            <button
              data-testid="clear-filters"
              onClick={clearAll}
              className="font-mono text-[11px] uppercase tracking-widest text-[#3B82F6] hover:underline"
            >
              Clear filters
            </button>
          )}
        </div>

        <div className="mt-10">
          {loading ? (
            <div data-testid="dashboard-loading" className="font-mono text-sm text-zinc-500">Loading…</div>
          ) : notes.length === 0 && !anyFilter ? (
            <div data-testid="dashboard-empty" className="border-2 border-dashed border-[#BFDBFE] p-10 sm:p-16 grid lg:grid-cols-2 gap-10 items-center bg-white">
              <div>
                <p className="font-mono text-xs uppercase tracking-widest text-zinc-500">/ Empty state</p>
                <h2 className="mt-3 font-heading font-black text-3xl sm:text-4xl tracking-tight">
                  Zero notes.<br />Infinite potential.
                </h2>
                <p className="mt-4 text-zinc-600 max-w-md">
                  Start by pasting a transcript, uploading a lecture recording, or dropping a URL.
                </p>
                <Link
                  to="/app/new"
                  className="inline-flex mt-6 items-center gap-2 font-mono text-xs uppercase tracking-widest px-5 py-3 bg-black text-white btn-brutal"
                >
                  <Plus className="w-4 h-4" /> Create your first note
                </Link>
              </div>
              <img src={emptyImg} alt="" className="w-full h-64 object-cover border-2 border-black" />
            </div>
          ) : notes.length === 0 ? (
            <div data-testid="no-results" className="border border-[#BFDBFE] bg-white p-10 text-center">
              <p className="font-heading font-bold text-xl">No notes match your filters</p>
              <p className="mt-2 text-zinc-500 text-sm">Try clearing filters or a different search.</p>
            </div>
          ) : (
            <div data-testid="notes-grid" className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-black/10 border border-black/10">
              {notes.map((n) => {
                const Icon = iconFor(n.source_type);
                const date = new Date(n.created_at).toLocaleDateString(undefined, {
                  month: "short", day: "numeric", year: "numeric",
                });
                return (
                  <div key={n.id} data-testid={`note-card-${n.id}`} className="group bg-white p-6 flex flex-col justify-between hover:bg-[#3B82F6]/10 transition-colors">
                    <Link to={`/app/note/${n.id}`} className="block">
                      <div className="flex items-center justify-between">
                        <div className="w-9 h-9 border-2 border-black flex items-center justify-center">
                          <Icon className="w-4 h-4" />
                        </div>
                        <span className="font-mono text-[10px] uppercase tracking-widest bg-black text-white px-2 py-1">
                          {n.format}
                        </span>
                      </div>
                      <h3 className="mt-5 font-heading font-bold text-xl leading-tight line-clamp-3">
                        {n.title}
                      </h3>
                      <p className="mt-3 text-sm text-zinc-600 line-clamp-2">
                        {n.notes?.one_line_summary || "—"}
                      </p>
                      {n.tags?.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-1">
                          {n.tags.slice(0, 4).map((t) => (
                            <span key={t} className="font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 bg-[#DBEAFE] text-black">
                              {t}
                            </span>
                          ))}
                        </div>
                      )}
                    </Link>
                    <div className="mt-6 flex items-center justify-between">
                      <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 inline-flex items-center gap-1">
                        <Calendar className="w-3 h-3" /> {date}
                      </span>
                      <button
                        data-testid={`delete-note-${n.id}`}
                        onClick={() => remove(n.id)}
                        className="p-2 hover:bg-[#FF3333] hover:text-white transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
